import React from 'react';
import _ from 'lodash';
import Helmet from 'react-helmet';
import ClipLoader from 'react-spinners/ClipLoader';
import Joi from 'joi-browser';

import dialogLoader from '../utilities/dialogLoader';
import _masterSlideLoader from '../utilities/masterSlideLoader';
import regex from '../utilities/validateRegex';
import { validationByJoi } from '../utilities/commonValidation';
import { PartnerNaviList } from '../components/list/PartnerNaviList';
import SessionStorageService from '../../src/services/utilities/SessionStorageService';
import {
  HEADER_CONSTANTS, // ヘッダータイトル
} from '../utilities/constants';
import { deleteControlCode } from '../utilities/stringUtil';
import { onceAsyncRequest } from '../utilities/commonUtil';
import {
  getPartnerCompanyInfo,
  propsToCompanyId,
} from '../utilities/partnerCompanyConfirmUtil';
import { MasterPageHeader } from '../components/PageHeader';
import { CloseEditView } from '../components/CloseEditView';
import { FormInformationDialog } from '../components/FormInformationDialog';
import { PartnerCompanyBranchForm } from '../components/PartnerCompanyBranchForm';
import { CompanyService } from '../services/Companies';
import PartnerCompanyBranchService from '../services/PartnerCompanyBranch';
import UsersApi from '../services/users/UsersApi';
import LocalStorageService from '../services/utilities/LocalStorageService';
import { messageUtility } from '../utilities/messageUtility';
import { isOneOwnerCompany } from '../utilities/partnerCompanyUtil';

const FORM_MODE = {
  EDIT: 1,
  REGISTER: 2,
};
const IS_BUILDEE_MANAGER = 1;

class PartnerCompanyBranchBase extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedCompanyName: '',
      requiredInput: {
        // 必須入力項目
        columns: [
          'name',
          'kana',
          'postal_code',
          'prefectures_code_id',
          'city_name',
          'tel',
        ], // 必須項目名
        count: 0, // 入力数
      },
      validate: {
        invalid: false, // 入力内容エラー
        messages: {}, // エラー内容
      },
      formValues: {
        name: '', // 会社名
        kana: '', // 支店名ふりがな
        postal_code: '', // 郵便番号
        prefectures_code_id: '', // 都道府県
        city_name: '', // 市区町村
        street_number: '', // 丁目・番地
        building_name: '', // 建物名・部屋番号等
        tel: '', // 電話番号
        fax: '', // FAX
      },
      isLoading: false,
      selectedModalId: '',
      expirationCompany: {},
    };
    this.updatingFalg = false; // 連打防止用フラグ
    this.events = {
      form: {
        setValue: this.setValue,
        validate: this.validate,
        submit: this.onSubmitForm,
      },
    };
    this.selectedCompanyBranchId = SessionStorageService.getSelectedPartnerCompanyBranchId();
    this.selectedCompanyId = _.get(
      this.props,
      'view.state.id',
      propsToCompanyId(this.props)
    );
    // 会社情報バリデーションパターン
    this.partnerCompanyBranchSchema = {
      name: Joi.string()
        .trim()
        .required()
        .max(255), // 会社名
      kana: Joi.string()
        .label('hiragana')
        .regex(regex.pattern.hiragana)
        .required()
        .max(512), // 会社名かな
      postal_code: Joi.string()
        .label('zip')
        .regex(regex.pattern.zip)
        .required(), // 郵便番号
      prefectures_code_id: Joi.number().required(), // 都道府県
      city_name: Joi.string()
        .label('city_name')
        // .regex(regex.pattern.must)
        .required()
        .max(512), // 市区町村
      street_number: Joi.string()
        .max(512)
        .allow(null, ''), // 丁目・番地
      building_name: Joi.string()
        .max(512)
        .allow(null, ''), // 建物名・部屋番号等
      tel: Joi.string()
        .label('tel')
        .regex(regex.pattern.tel)
        .required(), // 電話番号
      fax: Joi.string()
        .label('fax')
        .regex(regex.pattern.tel)
        // FAXは必須ではないのに空文字でバリデーションエラーが出るので「--」も許可
        .allow(null, '', '--'), // FAX
    };
    this.checkPermission();

    // 戻るダイアログ
    this.GoBack = (id, selectedPartnerCompanyBranchId) => {
      return {
        callback: {
          clear: () => {
            _masterSlideLoader.show('PartnerCompanyBranchPreview', {
              state: {
                id,
                selectedPartnerCompanyBranchId,
              },
            });
            dialogLoader.close();
          },
          close: () => {
            dialogLoader.close();
          },
        },
      };
    };
  }

  getExpiration = async () => {
    await CompanyService.expirations(this.selectedCompanyId).then(response => {
      if (!response.result) {
        return;
      }
      this.setState({
        expirationCompany: {
          ...this.state.expirationCompany,
          ...response.data.entities.company.expiration,
        },
      });
    });
  };

  componentDidMount() {
    this.setCompanyName();
    this.checkRequired();

    const { mode } = this.props;
    if (mode === FORM_MODE.EDIT) {
      if (!this.selectedCompanyBranchId) {
        window.location.replace('/error-permission');
      }
      this.getPartnerCompanyBranch(this.selectedCompanyBranchId).then(
        response => {
          if (response) {
            this.initForm(response);
          } else {
            this.showErrorDialog();
          }
        }
      );
    }
    this.getExpiration();
  }

  componentWillUnmount() {
    SessionStorageService.removeSelectedPartnerCompanyBranchId();
  }

  getPartnerCompanyBranch = async id => {
    if (id) {
      const { data } = await PartnerCompanyBranchService.show(
        this.selectedCompanyId,
        id
      );
      return _.get(data, 'entities.partner_company_branch');
    }
  };

  setCompanyName = () => {
    this.setState({ isLoading: true }, async () => {
      const { company, result, companyName } = await getPartnerCompanyInfo(
        this.selectedCompanyId
      );
      let selectedCompanyName = '';
      if (result) {
        // if is one owner company redirect to error-permission page
        if (isOneOwnerCompany(company.one_owner_flag)) {
          return window.location.replace('/error-permission');
        }
        selectedCompanyName = companyName;
      }
      this.setState({ selectedCompanyName, isLoading: false });
    });
  };

  initForm = data => {
    if (data) {
      this.setState({ formValues: data }, () => {
        this.validate();
      });
    }
  };

  /**
   * 戻るダイアログ表示
   */
  handleGoBackDialog = () => {
    dialogLoader.show(
      CloseEditView,
      this.GoBack(this.selectedCompanyId, this.selectedCompanyBranchId)
    );
  };

  /**
   * 値入力
   * @param name {string}
   * @param value {any}
   */
  setValue = (name, value) => {
    this.setState(
      {
        formValues: {
          ...this.state.formValues,
          [name]: deleteControlCode(value),
        },
      },
      () => {
        if (name === 'prefectures_code_id') {
          this.validate({}, name);
        }
      }
    );
  };

  /**
   * 入力値の有効判定
   * @return bool
   */
  validate = (e, targetName) => {
    const values = _.cloneDeep(this.state.formValues);
    const validate = _.cloneDeep(this.state.validate);

    const result = validationByJoi(
      Joi.object().keys(this.partnerCompanyBranchSchema),
      values
    );
    if (e || targetName) {
      const name = _.isEmpty(targetName) ? e.target.name : targetName;
      if (result.messages[name]) {
        validate.messages[name] = result.messages[name];
      } else if (validate.messages[name]) {
        delete validate.messages[name];
      }
    } else {
      _.each(this.state.requiredInput.columns, column => {
        if (result.messages[column]) {
          validate.messages[column] = result.messages[column];
        }
      });
    }
    validate.invalid = !_.isEmpty(validate.messages);
    this.setState({ validate }, () => {
      this.checkRequired();
    });
    return validate.invalid;
  };

  /**
   * 必須入力状況チェック
   */
  checkRequired = () => {
    const { formValues, requiredInput, validate } = this.state;
    let count = 0;
    // 会社情報
    _.each(requiredInput.columns, column => {
      if (formValues[column] && !validate.messages[column]) {
        count += 1;
      }
    });

    this.setState({
      requiredInput: {
        ...requiredInput,
        count,
      },
    });
  };

  onSubmitForm = () => {
    if (this.validate()) {
      return;
    }

    const { mode } = this.props;
    if (this.selectedCompanyBranchId && mode === FORM_MODE.EDIT) {
      this.update();
    } else {
      this.submit();
    }
  };

  showErrorDialog = () => {
    dialogLoader.show(FormInformationDialog, {
      title: 'エラーが発生しました',
      message:
        'エラーが発生しました。会社情報を開きなおし、再度操作を行ってください。',
      onClose: () => {
        dialogLoader.close();
        this.updatingFalg = false;
        _masterSlideLoader.show('CompanyPreview', {
          state: {
            id: this.selectedCompanyId,
          },
        });
      },
    });
  };

  showErrorCreateDialog = message => {
    dialogLoader.show(FormInformationDialog, {
      title: messageUtility.getMessage('E-001-402'),
      message,
      onClose: () => {
        dialogLoader.close();
        this.updatingFalg = false;
        _masterSlideLoader.show('CompanyPreview', {
          state: {
            id: this.selectedCompanyId,
          },
        });
      },
    });
  };

  update = onceAsyncRequest(async () => {
    // 連打防止対応
    if (this.updatingFalg) {
      return;
    }
    this.updatingFalg = true;
    const values = _.cloneDeep(this.state.formValues);
    delete values.block_flag;
    const response = await PartnerCompanyBranchService.updatePartnerCompanyBranch(
      this.selectedCompanyId,
      this.selectedCompanyBranchId,
      values
    );
    if (
      response.result &&
      _.has(response, 'data.entities.partner_company_branch')
    ) {
      dialogLoader.show(FormInformationDialog, {
        title: '支店更新完了',
        message: '支店の更新が完了しました。',
        onClose: () => {
          dialogLoader.close();
          this.updatingFalg = false;
          _masterSlideLoader.show('PartnerCompanyBranchPreview', {
            state: {
              id: this.selectedCompanyId,
              selectedPartnerCompanyBranchId:
                response.data.entities.partner_company_branch.id,
            },
          });
        },
      });
    } else {
      this.showErrorDialog();
      this.updatingFalg = false;
    }
  });

  submit = onceAsyncRequest(async () => {
    // 連打防止対応
    if (this.updatingFalg) {
      return;
    }
    this.updatingFalg = true;

    const response = await PartnerCompanyBranchService.partnerCompanyBranch(
      this.selectedCompanyId,
      this.state.formValues
    );
    if (
      response.result &&
      _.has(response, 'data.entities.partner_company_branch')
    ) {
      dialogLoader.show(FormInformationDialog, {
        title: '支店登録完了',
        message: '支店の登録が完了しました。',
        onClose: () => {
          dialogLoader.close();
          this.updatingFalg = false;
          _masterSlideLoader.show('PartnerCompanyBranchPreview', {
            state: {
              id: this.selectedCompanyId,
              selectedPartnerCompanyBranchId:
                response.data.entities.partner_company_branch.id,
            },
          });
        },
      });
    } else if ([400, 422].includes(response.status)) {
      let message = messageUtility.getMessage('E-001-433');
      const errorMessageOneOwnerCompany = messageUtility.getMessage(
        'E-001-434'
      );
      if (_.isEqual(response.data.message, errorMessageOneOwnerCompany)) {
        message = response.data.message;
      }
      this.showErrorCreateDialog(message);
    } else {
      this.updatingFalg = false;
    }
  });

  checkPermission = async () => {
    let redirectFlag = false;
    const ownCompany =
      Number(
        SessionStorageService.getSessionPartnerCompanyIdOfPrimeCompany()
      ) === Number(this.selectedCompanyId);
    if (!ownCompany) {
      redirectFlag = true;
    }
    const { data, result } = await UsersApi.fetchCompanyUserPermissions(
      this.selectedCompanyId,
      { buildee_manager_flg: IS_BUILDEE_MANAGER }
    );
    const isRegisterScreen = this.props.mode === FORM_MODE.REGISTER;
    if (!result && isRegisterScreen) {
      redirectFlag = true;
    }
    const companyUserPermissions = _.get(
      data,
      'entities.company_user_permissions'
    );
    if (_.isEmpty(companyUserPermissions) && isRegisterScreen) {
      redirectFlag = true;
    }
    const userId = LocalStorageService.getUserId();
    const isBuildeeManager = _.filter(
      companyUserPermissions,
      user => user.user_id === Number(userId)
    );
    if (_.isEmpty(isBuildeeManager) && isRegisterScreen) {
      redirectFlag = true;
    }
    if (redirectFlag) {
      window.location.replace('/error-permission');
    }
  };

  render() {
    const {
      selectedCompanyName,
      isLoading,
      formValues,
      validate,
      requiredInput,
    } = this.state;
    const { mode } = this.props;
    const title = `${selectedCompanyName} 会社情報`;
    const dismissButtonTitle = this.selectedCompanyBranchId
      ? '編集をやめる'
      : '登録をやめる';
    const submitButtonTitle = mode === FORM_MODE.EDIT ? '更新' : '登録';

    return (
      <div id='slide-view' className='contruction-slide-view show'>
        <Helmet>
          <title>
            {HEADER_CONSTANTS.title} - {title}
          </title>
        </Helmet>
        <div className='contruction-slide-view__view'>
          <MasterPageHeader
            title={title}
            loaded={!isLoading}
            loaderComponent={<ClipLoader size={20} color={'#EDB715'} />}
            onClose={this.handleGoBackDialog}
          />
          <div className='contruction-slide-view__body contruction-slide-view__body--height-fix'>
            <div className='contruction-detail-view'>
              <div className='contruction-detail-view__sidemenu'>
                <PartnerNaviList
                  isSelectedPartnerBranchInfomation={true}
                  isEditable={true}
                  selectedPartnerCompanyId={this.selectedCompanyId}
                  expirationCompany={this.state.expirationCompany}
                />
              </div>
              {!isLoading && (
                <div className='contruction-detail-view__body contruction-detail-view__body--fixed PartnerCompanyBranchEdit'>
                  <div className='PartnerCompanyBranchEdit__header'>
                    <span className='construction-detail-view__button'>
                      <button
                        className='btn--sub'
                        onClick={this.handleGoBackDialog}
                      >
                        {dismissButtonTitle}
                      </button>
                    </span>
                    <span className='construction-detail-view__button'>
                      <button
                        className={
                          validate.invalid
                            ? 'btn--effective btn--disabled'
                            : 'btn--effective'
                        }
                        onClick={() => this.events.form.submit()}
                        disabled={validate.invalid}
                      >
                        {submitButtonTitle}
                      </button>
                    </span>
                    <div className='construction-detail-view__end-item'>
                      <span className='construction-detail-view__end-item--error'>
                        {validate.invalid ? '入力エラーがあります。' : ''}
                      </span>
                      {`必須項目の入力数 ${requiredInput.count}/${_.size(
                        requiredInput.columns
                      )}`}
                    </div>
                  </div>
                  <div className='contruction-detail-view__bar' />
                  <div className='contruction-detail-view__main contruction-detail-view__main--fixed'>
                    <PartnerCompanyBranchForm
                      readOnly={false}
                      values={formValues}
                      messages={validate.messages}
                      events={this.events.form}
                    />
                  </div>
                </div>
              )}
            </div>
            {isLoading && <div className='indicator' />}
          </div>
        </div>
      </div>
    );
  }
}

export class PartnerCompanyBranchNew extends React.Component {
  render() {
    return (
      <PartnerCompanyBranchBase {...this.props} mode={FORM_MODE.REGISTER} />
    );
  }
}

export class PartnerCompanyBranchEdit extends React.Component {
  render() {
    return <PartnerCompanyBranchBase {...this.props} mode={FORM_MODE.EDIT} />;
  }
}



// WEBPACK FOOTER //
// ./src/pages/PartnerCompanyBranchEdit.jsx